/**
 * UGC Generation Toast Configuration Constants
 * All messages, progress states, and animation settings
 */

// Progress animation configuration constants
export const ProgressConfig = {
  increment: 0.5,        // Increment 0.5% every interval (smooth animation)
  intervalMs: 500,      // 500ms interval for smooth visible progress
  logInterval: 5        // Log progress every 5% to reduce console spam
};

// Progress state configuration for hybrid progress animation
export interface ProgressState {
  range: [number, number];
  safetyCeiling: number;
  messages: Array<{ threshold: number; text: string }>;
}

// Progress states with messages for Avatar flow (withAvatar: true)
export const ProgressStatesWithAvatar: ProgressState[] = [
  {
    range: [0, 30],
    safetyCeiling: 29,
    messages: [
      { threshold: 0, text: 'Fetching avatar details...' },
      { threshold: 5, text: 'Analyzing details...' },
      { threshold: 10, text: 'Preparing avatar for creation...' },
      { threshold: 20, text: 'Creating your avatar' }
    ]
  },
  {
    range: [30, 50],
    safetyCeiling: 48,
    messages: [
      { threshold: 30, text: 'Assembling assets...' },
      { threshold: 35, text: 'Analyzing assets...' },
      { threshold: 40, text: 'Building your slideshow' }
    ]
  },
  {
    range: [50, 65],
    safetyCeiling: 63,
    messages: [
      { threshold: 50, text: 'Attaching avatar...' },
      { threshold: 55, text: 'Syncing elements...' },
      { threshold: 60, text: 'Rendering avatar overlay' }
    ]
  },
  {
    range: [65, 80],
    safetyCeiling: 78,
    messages: [
      { threshold: 65, text: 'Processing script...' },
      { threshold: 70, text: 'Syncing text...' },
      { threshold: 75, text: 'Generating captions' }
    ]
  },
  {
    range: [80, 100],
    safetyCeiling: 98,
    messages: [
      { threshold: 80, text: 'Optimizing video...' },
      { threshold: 85, text: 'Rendering final output...' },
      { threshold: 95, text: 'Finalizing your video' }
    ]
  }
];

// Progress states with messages for Non-Avatar flow (withAvatar: false)
export const ProgressStatesWithoutAvatar: ProgressState[] = [
  {
    range: [0, 30],
    safetyCeiling: 14,
    messages: [
      { threshold: 0, text: 'Starting your project...' },
      { threshold: 5, text: 'Gathering media files...' },
      { threshold: 10, text: 'Gathering your content' }
    ]
  },
  {
    range: [30, 45],
    safetyCeiling: 34,
    messages: [
      { threshold: 30, text: 'Analyzing content...' },
      { threshold: 35, text: 'Assembling scenes...' },
      { threshold: 40, text: 'Building your video' }
    ]
  },
  {
    range: [45, 60],
    safetyCeiling: 54,
    messages: [
      { threshold: 45, text: 'Analyzing your script...' },
      { threshold: 50, text: 'Generating subtitles...' },
      { threshold: 55, text: 'Adding subtitles…' }
    ]
  },
  {
    range: [60, 75],
    safetyCeiling: 69,
    messages: [
      { threshold: 60, text: 'Fetching voice details...' },
      { threshold: 65, text: 'Generating voice over…' },
      { threshold: 70, text: 'Adding voice-over…' }
    ]
  },
  {
    range: [75, 85],
    safetyCeiling: 79,
    messages: [
      { threshold: 75, text: 'Capturing Image...' },
      { threshold: 80, text: 'Generating thumbnail' }
    ]
  },
  {
    range: [85, 100],
    safetyCeiling: 98,
    messages: [
      { threshold: 85, text: 'Syncing audio with subtitles...' },
      { threshold: 90, text: 'Optimizing video...' },
      { threshold: 95, text: 'Saving your video' }
    ]
  }
];

// Toast UI Messages for Avatar flow (withAvatar: true)
export const ToastMessagesWithAvatar = {
  initial: 'Fetching avatar details...',
  completed: 'Your video is ready!',
  estimatedTime: 'This might take 5-10 mins',
  headerGenerating: 'Generating UGC Video',
  headerCompleted: 'UGC Ad Generated'
};

// Toast UI Messages for Non-Avatar flow (withAvatar: false)
export const ToastMessagesWithoutAvatar = {
  initial: 'Starting your project...',
  completed: 'Your video is ready!',
  estimatedTime: 'This might take 5-10 mins',
  headerGenerating: 'Generating UGC Video',
  headerCompleted: 'UGC Ad Generated'
};

// Toast UI Messages for the AI Video flow.
//
// AI Video generation emits NO progress events from the backend — only a terminal
// READY / FAILED. So the message is fixed (no per-progress transitions) and the bar
// is a timed ramp that the completion event snaps to 100.
export const ToastMessagesAiVideo = {
  initial: 'Generating your video…',
  completed: 'Your video is ready!',
  failed: 'Video generation failed. Please try again.',
  estimatedTime: 'Rendering video...',
  headerGenerating: 'Generating AI Video',
  headerCompleted: 'AI Video Generated'
};

// AI Video ramp: climb steadily from 0 to `ceiling` over `rampDurationMs`, then hold
// at the ceiling until the completion SignalR event jumps it to 100.
export const AiVideoProgressConfig = {
  ceiling: 95,
  rampDurationMs: 360000 // ~6 minutes, the observed average
};

// Helper functions to get correct constants based on withAvatar flag
export function getProgressStates(withAvatar: boolean): ProgressState[] {
  return withAvatar ? ProgressStatesWithAvatar : ProgressStatesWithoutAvatar;
}

export function getToastMessages(withAvatar: boolean) {
  return withAvatar ? ToastMessagesWithAvatar : ToastMessagesWithoutAvatar;
}

// Backward compatibility - default to avatar flow
export const ProgressStates = ProgressStatesWithAvatar;
export const ToastMessages = ToastMessagesWithAvatar;
